import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import MainLayout from "../../../components/layout/MainLayout";
import { useMasterData } from "../../../hooks/useMasterData";
import { usePermissions } from "../../../contexts/PermissionContext";
import { feesApi } from "../api/feesApi";

type StudentOption = {
  studentId: string;
  firstName: string;
  lastName: string;
  scholarNumber?: string;
  class?: { id: string; name: string };
  section?: { id: string; name: string };
};

type PaymentModeOption = {
  id: string;
  name: string;
  code: string;
  isOnline: boolean;
  requiresReference: boolean;
};

type StudentRow = {
  studentId: string;
  name: string;
  scholarNumber: string;
  classSection: string;
  totalFee: number;
  paid: number;
  due: number;
  amountToPay: string;
  selected: boolean;
  loadStatus: "READY" | "FAILED";
  submitStatus: "IDLE" | "SUCCESS" | "FAILED";
  reason?: string;
  receiptId?: string | null;
  receiptNo?: string | null;
};

type BulkRowResult = {
  studentId: string;
  status: "SUCCESS" | "FAILED";
  reason?: string;
  receiptId?: string | null;
  receiptNo?: string | null;
};

const amount = (value: any) => Number(value || 0);

const BulkFeeCollectionPage: React.FC = () => {
  const { masters, fetchAcademicSessions, fetchSections, fetchMasters } = useMasterData();
  const { hasAnyPermission } = usePermissions();

  const [sessionId, setSessionId] = useState("");
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [sections, setSections] = useState<Array<{ id: string; name: string }>>([]);

  const [paymentModes, setPaymentModes] = useState<PaymentModeOption[]>([]);
  const [paymentMode, setPaymentMode] = useState("");
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [referenceNo, setReferenceNo] = useState("");
  const [remarks, setRemarks] = useState("");
  const [chequeNo, setChequeNo] = useState("");
  const [chequeBank, setChequeBank] = useState("");
  const [chequeDate, setChequeDate] = useState(new Date().toISOString().slice(0, 10));

  const [rows, setRows] = useState<StudentRow[]>([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultMessage, setResultMessage] = useState<string | null>(null);

  const canView = useMemo(
    () => hasAnyPermission(["FEES_DEPOSIT_ALL_VIEW", "FEES_DEPOSIT_VIEW", "FEE_VIEW", "FEE_MANAGE"]),
    [hasAnyPermission],
  );
  const canCreate = useMemo(
    () => hasAnyPermission(["FEES_DEPOSIT_ALL_CREATE", "FEES_DEPOSIT_CREATE", "FEE_MANAGE"]),
    [hasAnyPermission],
  );

  const selectedPaymentMode = useMemo(
    () => paymentModes.find((m) => m.id === paymentMode),
    [paymentModes, paymentMode],
  );
  const isChequeMode = (selectedPaymentMode?.code || "").toUpperCase() === "CHEQUE";
  const isReferenceRequired = !!selectedPaymentMode?.requiresReference;

  const allSelectable = rows.filter((r) => r.loadStatus === "READY");
  const allSelected = allSelectable.length > 0 && allSelectable.every((r) => r.selected);
  const selectedRows = rows.filter((r) => r.selected && r.loadStatus === "READY");

  useEffect(() => {
    fetchMasters();
    fetchAcademicSessions();
  }, [fetchMasters, fetchAcademicSessions]);

  useEffect(() => {
    const active = masters.academicSessions.find((s) => s.isActive);
    if (active && !sessionId) setSessionId(active.id);
  }, [masters.academicSessions, sessionId]);

  useEffect(() => {
    let cancelled = false;
    const loadPaymentModes = async () => {
      try {
        const list = await feesApi.paymentModes({ isActive: true });
        const modes = Array.isArray(list) ? list : [];
        if (cancelled) return;
        setPaymentModes(modes);
        setPaymentMode((prev) => {
          if (prev && modes.some((m: PaymentModeOption) => m.id === prev)) return prev;
          return modes[0]?.id || "";
        });
      } catch {
        if (!cancelled) {
          setPaymentModes([]);
          setPaymentMode("");
        }
      }
    };

    loadPaymentModes();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!classId) {
        setSections([]);
        setSectionId("");
        return;
      }
      const next = await fetchSections(classId);
      if (!cancelled) {
        setSections(Array.isArray(next) ? next : []);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [classId, fetchSections]);

  const loadStudents = async () => {
    if (!sessionId || !classId) {
      setError("Academic Session and Class are required");
      return;
    }

    setLoadingStudents(true);
    setError(null);
    setResultMessage(null);
    setRows([]);

    try {
      const students = await feesApi.students({
        sessionId,
        classId,
        sectionId: sectionId || undefined,
        limit: 1000,
      });
      const studentList: StudentOption[] = Array.isArray(students) ? students : [];

      const demandResults = await Promise.all(
        studentList.map(async (s) => {
          try {
            const summary = await feesApi.getSummary(s.studentId, sessionId);
            return {
              studentId: s.studentId,
              ok: true,
              row: {
                studentId: s.studentId,
                name: `${s.firstName} ${s.lastName || ""}`.trim(),
                scholarNumber: s.scholarNumber || "-",
                classSection: `${s.class?.name || "-"} - ${s.section?.name || "-"}`,
                totalFee: amount(summary?.totalFee),
                paid: amount(summary?.totalPaid),
                due: amount(summary?.finalDue),
                amountToPay: amount(summary?.finalDue) > 0 ? amount(summary?.finalDue).toFixed(2) : "",
                selected: amount(summary?.finalDue) > 0,
                loadStatus: "READY" as const,
                submitStatus: "IDLE" as const,
                reason: undefined,
                receiptId: null,
                receiptNo: null,
              },
            };
          } catch (e: any) {
            return {
              studentId: s.studentId,
              ok: false,
              row: {
                studentId: s.studentId,
                name: `${s.firstName} ${s.lastName || ""}`.trim(),
                scholarNumber: s.scholarNumber || "-",
                classSection: `${s.class?.name || "-"} - ${s.section?.name || "-"}`,
                totalFee: 0,
                paid: 0,
                due: 0,
                amountToPay: "",
                selected: false,
                loadStatus: "FAILED" as const,
                submitStatus: "FAILED" as const,
                reason: e?.response?.data?.message || e?.message || "Failed to load fee data",
                receiptId: null,
                receiptNo: null,
              },
            };
          }
        }),
      );

      setRows(demandResults.map((r) => r.row));
      if (!demandResults.length) {
        setResultMessage("No students found for selected filter.");
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to load students");
    } finally {
      setLoadingStudents(false);
    }
  };

  const toggleSelectAll = (checked: boolean) => {
    setRows((prev) =>
      prev.map((r) =>
        r.loadStatus === "READY"
          ? { ...r, selected: checked }
          : r,
      ),
    );
  };

  const updateRowAmount = (studentId: string, nextAmount: string) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.studentId !== studentId) return r;
        return {
          ...r,
          amountToPay: nextAmount,
          submitStatus: "IDLE",
          reason: undefined,
        };
      }),
    );
  };

  const toggleRow = (studentId: string, checked: boolean) => {
    setRows((prev) =>
      prev.map((r) => (r.studentId === studentId ? { ...r, selected: checked } : r)),
    );
  };

  const submitBulkCollection = async () => {
    setError(null);
    setResultMessage(null);

    if (!sessionId || !classId) {
      setError("Academic Session and Class are required");
      return;
    }
    if (!paymentMode) {
      setError("Payment mode is required");
      return;
    }
    if (isReferenceRequired && !referenceNo.trim()) {
      setError(`Reference number is required for ${selectedPaymentMode?.name || "selected payment mode"}`);
      return;
    }
    if (isChequeMode && (!chequeNo || !chequeBank || !chequeDate)) {
      setError("Cheque number, bank name and cheque date are required");
      return;
    }

    const students = selectedRows
      .map((r) => ({ studentId: r.studentId, amount: amount(r.amountToPay) }))
      .filter((r) => r.amount > 0);

    if (!students.length) {
      setError("Select at least one student and enter amount greater than 0");
      return;
    }

    setSubmitting(true);
    try {
      const response = await feesApi.bulkDeposit({
        sessionId,
        paymentMode,
        paymentDate,
        referenceNo: referenceNo || undefined,
        remarks: remarks || undefined,
        chequeNo: isChequeMode ? chequeNo : undefined,
        chequeBank: isChequeMode ? chequeBank : undefined,
        chequeDate: isChequeMode ? chequeDate : undefined,
        students,
      });

      const results: BulkRowResult[] = Array.isArray(response?.results)
        ? response.results
        : [];
      const mapByStudent = new Map<string, BulkRowResult>(
        results.map((r) => [r.studentId, r]),
      );

      setRows((prev) =>
        prev.map((r) => {
          const hit = mapByStudent.get(r.studentId);
          if (!hit) return r;
          return {
            ...r,
            submitStatus: hit.status === "SUCCESS" ? "SUCCESS" : "FAILED",
            reason: hit.reason,
            receiptId: hit.receiptId || null,
            receiptNo: hit.receiptNo || null,
          };
        }),
      );

      setResultMessage(`Processed ${response?.total || students.length}. Success: ${response?.success || 0}, Failed: ${response?.failed || 0}`);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Bulk collection failed");
    } finally {
      setSubmitting(false);
    }
  };

  const openReceipt = (receiptId?: string | null) => {
    if (!receiptId) return;
    feesApi
      .getReceiptPdfBlob(receiptId)
      .then((file) => {
        const url = URL.createObjectURL(file.blob);
        const win = window.open(url, "_blank");
        if (!win) {
          const a = document.createElement("a");
          a.href = url;
          a.download = file.filename;
          a.click();
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      })
      .catch((e: any) => {
        setError(e?.response?.data?.message || e?.message || "Failed to open receipt");
      });
  };

  const downloadSuccessfulReceiptsZip = async () => {
    const receiptIds = rows
      .filter((r) => r.submitStatus === "SUCCESS" && !!r.receiptId)
      .map((r) => r.receiptId as string);

    if (!receiptIds.length) {
      setError("No successful receipts available for ZIP download");
      return;
    }

    try {
      const file = await feesApi.getReceiptsZipBlob(receiptIds);
      const url = URL.createObjectURL(file.blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to download receipts ZIP");
    }
  };

  return (
    <MainLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
          Bulk Fee Collection
        </Typography>

        {!canView && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You do not have view permission for bulk fee collection.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {resultMessage && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {resultMessage}
          </Alert>
        )}

        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Academic Session"
                value={sessionId}
                onChange={(e) => setSessionId(e.target.value)}
                required
              >
                {masters.academicSessions.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name} {s.isActive ? "(Active)" : ""}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Class"
                value={classId}
                onChange={(e) => {
                  setClassId(e.target.value);
                  setSectionId("");
                }}
                required
              >
                {masters.classes.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Section (Optional)"
                value={sectionId}
                onChange={(e) => setSectionId(e.target.value)}
                disabled={!classId}
              >
                <MenuItem value="">All</MenuItem>
                {sections.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                onClick={loadStudents}
                disabled={loadingStudents || !sessionId || !classId || !canView}
              >
                {loadingStudents ? "Loading Students..." : "Load Students"}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Global Payment Settings
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Payment Mode"
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value)}
                required
              >
                {paymentModes.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Payment Date"
                fullWidth
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label={isReferenceRequired ? "Reference No" : "Reference No (Optional)"}
                fullWidth
                value={referenceNo}
                onChange={(e) => setReferenceNo(e.target.value)}
                required={isReferenceRequired}
                helperText={
                  selectedPaymentMode?.isOnline
                    ? "Online payment mode selected"
                    : selectedPaymentMode?.requiresReference
                      ? "Reference required for this payment mode"
                      : ""
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Remarks (Optional)"
                fullWidth
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </Grid>

            {isChequeMode && (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Cheque No"
                    fullWidth
                    value={chequeNo}
                    onChange={(e) => setChequeNo(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Bank Name"
                    fullWidth
                    value={chequeBank}
                    onChange={(e) => setChequeBank(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Cheque Date"
                    fullWidth
                    type="date"
                    value={chequeDate}
                    onChange={(e) => setChequeDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Student Fee Rows
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                onClick={downloadSuccessfulReceiptsZip}
                disabled={!rows.some((r) => r.submitStatus === "SUCCESS" && !!r.receiptId)}
              >
                Download Receipts ZIP
              </Button>
              <Button
                variant="contained"
                onClick={submitBulkCollection}
                disabled={submitting || !rows.length || !canCreate}
              >
                {submitting ? "Collecting..." : "Collect Fees (Bulk)"}
              </Button>
            </Stack>
          </Stack>

          {!canCreate && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              You do not have create permission for bulk fee collection.
            </Alert>
          )}

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={!allSelected && selectedRows.length > 0}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                  />
                </TableCell>
                <TableCell>Student Name</TableCell>
                <TableCell>Scholar No</TableCell>
                <TableCell>Class - Section</TableCell>
                <TableCell align="right">Total Fee</TableCell>
                <TableCell align="right">Paid</TableCell>
                <TableCell align="right">Due</TableCell>
                <TableCell align="right">Amount to Pay</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Receipt</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.studentId}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={r.selected}
                      disabled={r.loadStatus !== "READY"}
                      onChange={(e) => toggleRow(r.studentId, e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>{r.name}</TableCell>
                  <TableCell>{r.scholarNumber}</TableCell>
                  <TableCell>{r.classSection}</TableCell>
                  <TableCell align="right">{r.totalFee.toFixed(2)}</TableCell>
                  <TableCell align="right">{r.paid.toFixed(2)}</TableCell>
                  <TableCell align="right">{r.due.toFixed(2)}</TableCell>
                  <TableCell align="right" sx={{ width: 170 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={r.amountToPay}
                      onChange={(e) => updateRowAmount(r.studentId, e.target.value)}
                      disabled={r.loadStatus !== "READY"}
                      inputProps={{ min: 0, step: "0.01" }}
                    />
                  </TableCell>
                  <TableCell>
                    {r.loadStatus === "FAILED" ? (
                      <Chip color="error" label="Load Failed" size="small" />
                    ) : r.submitStatus === "SUCCESS" ? (
                      <Chip color="success" label="Success" size="small" />
                    ) : r.submitStatus === "FAILED" ? (
                      <Chip color="error" label="Failed" size="small" />
                    ) : (
                      <Chip color="default" label="Pending" size="small" />
                    )}
                    {r.reason ? (
                      <Typography variant="caption" display="block" color="error.main">
                        {r.reason}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    {r.receiptId ? (
                      <Button size="small" onClick={() => openReceipt(r.receiptId)}>
                        {r.receiptNo || "Download"}
                      </Button>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Box>
    </MainLayout>
  );
};

export default BulkFeeCollectionPage;
